import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { fetchInsights, subscribeNewsletter } from "@/lib/api";

const CATEGORIES = ["All", "AI", "Marketing Strategy", "Website Design", "Social Media", "Content", "Automation", "Opinion"];

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");

  useEffect(() => {
    document.title = "Insights | Track Digital";
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      fetchInsights({ category, search })
        .then(setPosts)
        .catch(() => toast.error("Could not load insights"))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [category, search]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.status === "already_subscribed" ? "You are already subscribed" : "Thanks for subscribing");
      setEmail("");
    } catch {
      toast.error("Something went wrong");
    }
  };

  const isDefaultView = category === "All" && !search;
  const featured = posts.find((p) => p.featured) || posts[0];
  const rest = isDefaultView ? posts.filter((p) => p.slug !== featured?.slug) : posts;

  return (
    <main data-testid="insights-page">
      <section className="pt-36 md:pt-44 pb-12">
        <div className="td-container">
          <Reveal>
            <span className="td-label block mb-6">Insights</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] max-w-4xl">
              Practical thinking on marketing, AI, websites and systems.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Featured */}
      {featured && category === "All" && !search && (
        <section className="pb-12">
          <div className="td-container">
            <Reveal>
              <Link
                to={`/insights/${featured.slug}`}
                data-testid="featured-insight"
                className="group grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-[#fafafa] rounded-3xl p-6 md:p-8 border border-[#eee] hover:border-[#d3d3d3] transition-colors"
              >
                <div className="lg:col-span-6">
                  <div className="aspect-[16/10] rounded-2xl overflow-hidden">
                    <img src={featured.cover} alt={featured.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                </div>
                <div className="lg:col-span-6">
                  <span className="td-label block mb-4">Featured · {featured.category}</span>
                  <h2 className="text-2xl md:text-3xl font-semibold leading-tight mb-4 group-hover:text-[#7C6CF0] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-[#6b6b6b] leading-relaxed mb-6">{featured.excerpt}</p>
                  <span className="inline-flex items-center gap-2 text-[#7C6CF0] font-medium">
                    Read article <ArrowUpRight size={18} />
                  </span>
                </div>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="pb-12">
        <div className="td-container flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-sm transition-colors ${
                  category === c ? "bg-black text-white" : "border border-[#e5e5e5] text-[#4a4a4a] hover:border-black"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 border border-[#e5e5e5] rounded-full px-4 py-2 md:w-64">
            <Search size={16} className="text-[#8a8a8a]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search insights"
              data-testid="insights-search"
              className="bg-transparent outline-none text-sm flex-1"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-20">
        <div className="td-container">
          {loading ? (
            <p className="text-[#8a8a8a]">Loading insights...</p>
          ) : rest.length === 0 ? (
            <p className="text-[#8a8a8a]" data-testid="no-insights">No insights found.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rest.map((p, i) => (
                <Reveal key={p.slug} delay={(i % 3) * 70}>
                  <Link to={`/insights/${p.slug}`} data-testid={`insight-card-${p.slug}`} className="group block">
                    <div className="aspect-[16/10] rounded-2xl overflow-hidden mb-5 border border-[#eee]">
                      <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                    </div>
                    <span className="td-label block mb-3">{p.category} · {p.read_time}</span>
                    <h3 className="text-xl font-semibold leading-snug mb-2 group-hover:text-[#7C6CF0] transition-colors">{p.title}</h3>
                    <p className="text-[#6b6b6b] text-[15px] leading-relaxed">{p.excerpt}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="td-container pb-24">
        <div className="rounded-3xl px-8 py-14 md:px-16 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" style={{ background: "#ECE9FF" }}>
          <div>
            <span className="td-label block mb-4">Newsletter</span>
            <h2 className="text-black text-2xl md:text-3xl font-semibold leading-tight">
              Practical marketing thinking, occasionally.
            </h2>
          </div>
          <form onSubmit={handleSubscribe} data-testid="insights-newsletter-form" className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              data-testid="insights-newsletter-input"
              className="flex-1 rounded-full bg-white border border-[#d9d4f7] px-6 py-3.5 text-black placeholder:text-[#a0a0a0] outline-none focus:border-[#7C6CF0] transition-colors"
            />
            <button type="submit" data-testid="insights-newsletter-submit" className="td-btn td-btn-primary whitespace-nowrap">
              Subscribe <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
